/**
 * The crosshair bus that keeps grouped charts in step.
 *
 * ## Why this is refs and callbacks rather than React state
 *
 * A crosshair moves on every pointer event — dozens per second while a hand is
 * moving. Holding the position in `useState` would re-render the workspace,
 * every panel in it and every chart, at that rate, which turns a smooth line
 * into a stuttering one and burns a core doing it.
 *
 * So subscribers register a plain function and are called directly. Nothing
 * here touches React's render cycle at all.
 *
 * ## The loop this is shaped to prevent
 *
 * Chart A's pointer move → set the crosshair on chart B → **B's own move
 * handler fires**, because Lightweight Charts does not distinguish a
 * programmatic position from a pointer one → B echoes to A → A echoes to B,
 * until the pointer stops.
 *
 * Two things stop it, and both are needed:
 *
 * 1. `crosshairTargets` never returns the source, so an echo cannot come
 *    straight back the way it went.
 * 2. `applying` is raised for the duration of a delivery, and `emit` returns
 *    early while it is up. That covers the longer path — A → B → C → A —
 *    which the first alone does not.
 */
import type { CrosshairTarget } from '@aegis/core';

export interface CrosshairPosition {
  /** Epoch seconds, as Lightweight Charts uses. */
  readonly time: number;
  /** Null when the pointer is off the price scale, or price must not travel. */
  readonly price: number | null;
}

/** What a chart does when a grouped chart's crosshair moves. */
export type CrosshairListener = (at: CrosshairPosition | null) => void;

export interface CrosshairBus {
  /** Register a chart. Returns its unsubscribe. */
  readonly subscribe: (paneId: string, listener: CrosshairListener) => () => void;
  /** Announce a move. `null` means the pointer left the chart. */
  readonly emit: (
    from: string, at: CrosshairPosition | null, targets: readonly CrosshairTarget[],
  ) => void;
}

export function createCrosshairBus(): CrosshairBus {
  const listeners = new Map<string, CrosshairListener>();
  /*
    Raised while a position is being delivered.

    Not a per-pane flag: the loop worth stopping is A → B → C → A, and a flag
    on B alone does not see it. One flag for the whole delivery makes any echo
    raised during it a no-op, whatever path it took.
  */
  let applying = false;

  return {
    subscribe(paneId, listener) {
      listeners.set(paneId, listener);
      return () => {
        // Guarded: a remount can register a NEW listener under this id before
        // the old effect's cleanup runs, and an unguarded delete would drop
        // the live one and leave that chart silently out of its group.
        if (listeners.get(paneId) === listener) listeners.delete(paneId);
      };
    },

    emit(from, at, targets) {
      if (applying) return;
      applying = true;
      try {
        for (const target of targets) {
          const listener = listeners.get(target.paneId);
          if (!listener) continue;
          listener(at === null || target.withPrice
            ? at
            // Price stripped rather than the whole position dropped: the time
            // is still comparable across instruments and is most of the value.
            : { time: at.time, price: null });
        }
      } finally {
        // `finally`, so a listener that throws does not wedge the flag up and
        // silently kill every future move on the whole screen.
        applying = false;
      }
    },
  };
}
